import * as React from 'react'
import { StoreProps } from '../../../../services/stores/casting.store'
import moment from 'moment'
import { isSameDay, utilRange } from '../../../../services/utils/utils.service'
import { openableDateToWorkingDay } from '../../../../services/utils/openables.service'
import { onlyUniqueByReference } from '../../../../services/utils/lodash.service'
import { FloatingContainerTitle } from '../casting-styled.component'
import { StyledDayPicker, getDayPickerDateLocale } from '../../../shared/styled-day-picker.component'
import { DateRange } from 'react-day-picker'
import SlotAssignablePickerButton from '../assignables/slot-assignable-picker-button.component'
import { I18nService } from '../../../../services/utils/i18n.service'

type Props = StoreProps & {
  startDate: Date
  initialMonth: Date
  fromMonth: Date
  isStartDayDisabled: (date: Date, opts?: { duration?: number }) => boolean
}

const i18nScoped = new I18nService(require('./casting-start-date-multiple-days-slot.component.yml'))

export default function CastingStartDateMultipleDaysSlotComponent ({ store, data, startDate, initialMonth, fromMonth, isStartDayDisabled }: Props) {
  const { slots, offeredService: os, backofficeSelectAssignableForSlots } = data
  const dateRange = { from: startDate, to: startDate && slots[0].duration ? moment(startDate).add(slots[0].duration - 1, 'seconds').toDate() : null }
  const selectedWorkingDays = dateRange.from && dateRange.to && utilRange(dateRange.from.valueOf(), dateRange.to.valueOf() + 1, 3600 * 1000)
    .map(timestamp => openableDateToWorkingDay(new Date(timestamp)))
    .filter(onlyUniqueByReference)

  /**
   * Used to disable days from a day-range picker for long events (> 24h).
   * If startDate is not set, the `date` is checked as a potential start date.
   * Else, date is check as a potential new start date (if before) or end date (if after), such way duration_max is not exceeded.
   */
  const isDayDisableForMultipleDaysEvent = React.useCallback((date: Date, startDate: Date) => {
    // multi day events cannot start today, because we suppose they starts at midnight.
    if (isSameDay(date, new Date())) return true
    
    const potentialStart = startDate && startDate.valueOf() < date.valueOf() ? startDate : date
    const potentialEnd = startDate ? (startDate.valueOf() > date.valueOf() ? startDate : date) : new Date(date.getTime() + os.duration_min * 1000)

    // check if start date is acceptable considering the resulting duration
    return isStartDayDisabled(potentialStart, { duration: (potentialEnd.valueOf() - potentialStart.valueOf()) / 1000 + 1 }) &&
      isStartDayDisabled(date, { duration: os.duration_min })
  }, [isStartDayDisabled, os.duration_min])

  const setMultipleDaysSlot = React.useCallback((newRange: DateRange) => {
    newRange ||= { from: null, to: null }

    // prefer 'start of next day' instead of 'end of day'.
    if (newRange.to) newRange.to = moment(newRange.to).add(1, 'day').toDate()
      
    // check if the new range is valid regarding working days. Else, reset the range at this new date.
    const rangeDuration = newRange.to && ((newRange.to.valueOf() - newRange.from.valueOf()) / 1000)
    
    if (newRange.to && isStartDayDisabled(newRange.from, { duration: rangeDuration })) {
      newRange.from = newRange.to = null
    }

    store.setSlots([{
      ...slots[0],
      start_date: newRange.from ? moment(newRange.from).format('YYYY-MM-DD') : null,
      duration: newRange.to ? (newRange.to.valueOf() - newRange.from.valueOf()) / 1000 : null,
      allowed_patterns: !newRange.from || !newRange.to ? [] : [{
        day_id: newRange.from.getDay(),
        from_hour: '00:00',
        until_hour: '00:00',
        additional_days: moment(newRange.to).diff(moment(newRange.from), 'days')
      }]
    }])
  }, [isStartDayDisabled, slots, store])

  return <React.Fragment>
    <FloatingContainerTitle>{ i18nScoped.t('continous_day_range') }</FloatingContainerTitle>

    <p className="stepTooltip-subTitle u-mbm">
      { i18nScoped.tFragment('duration_in_days_should_be', { 
        duration_interval: <b className="u-txtClr3">{ `${i18nScoped.t('between')} ${os.duration_min / 24 / 3600} ${i18nScoped.t('and')} ${os.duration_max / 24 / 3600} ${i18nScoped.t('days')}` }</b> 
      }) }
    </p>

    <StyledDayPicker
      mode='range'
      defaultMonth={ initialMonth }
      startMonth={ fromMonth }
      locale={ getDayPickerDateLocale() }
      selected={ dateRange }
      disabled={ day => isDayDisableForMultipleDaysEvent(moment(day).startOf('day').toDate(), startDate) }
      min={ Math.round(os.duration_min / 24 / 3600) - 1 }
      max={ Math.round(os.duration_max / 24 / 3600) - 1 }
      onSelect={ setMultipleDaysSlot } />

    { selectedWorkingDays && backofficeSelectAssignableForSlots && <div style={{ marginTop: '1em' }}>
      <SlotAssignablePickerButton store={ store } data={ data } slot={ slots[0] } titleStyle={{ display: 'none' }} />
    </div> }

    { selectedWorkingDays && os.multi_opening_hours && <div style={{ padding: '1em 0' }}>
      <p><b>{ i18nScoped.t('offered_service_timetable_for_selected_days') }</b></p>

      <table style={{ width: '100%' }}>
        <tbody>
          { selectedWorkingDays.map(day => <tr key={ day }>
            <td style={{ padding: '0.5em', textTransform: 'capitalize' }}>{ moment().day(day).format('dddd') }</td>
            <td style={{ padding: '0.5em' }}>{ os.multi_opening_hours[day]?.map((interval, index) => <div key={ index }>{ interval[0] } - { interval[1] }</div>) }</td>
          </tr>) }
        </tbody>
      </table>
    </div> }
  </React.Fragment>
}